/**
 * Die Glocke in drei Antworten: „Braucht dich“, „Fertig“, „Routine“. (06./07.10.2026)
 *
 * Martin, 06.10.: *„Weiß ich noch nicht so genau, was ich mit dem
 * Glockensymbol für dich hinterlegt machen soll. Das ist für mich schwer
 * brauchbar … es sind schon sehr viele Informationen, die ich nicht wirklich
 * verwerten kann.“* Am 05.10.: *„nur sehen, was gerade noch am Bauen ist,
 * was fertig ist; viele Details brauche ich nicht“.* Und am 07.10., nach der
 * Karte mit drei Gruppen: *„Das ist unbedienbar … ich weiß ehrlich gesagt
 * nicht, was ich mit der Liste anfangen soll.“*
 *
 * ## Was am Bestand stand (Konto 1, 07.10.)
 *
 * Viermal „Vespera hat versucht anzurufen“ (vor 16 Stunden, 20 Stunden, gestern,
 * gestern), zweimal „Aufnahme unvollständig“, dazu „12 frühere“ — in einer
 * Gruppe „Für dich“, in der niemand etwas tun musste. Darunter „Fertig“ mit
 * „Auftrag 8343 auf Webseite 23“ und „Ticket #2875“ und „19 frühere“. Gleiches
 * stand mehrfach da, und was längst keiner Handlung mehr bedurfte, hielt den
 * Punkt an der Glocke.
 *
 * ## Die Regel — hier und nur hier
 *
 *   Braucht dich  NUR, was Martin tun oder entscheiden muss: eine Stimme, die
 *                 Vespera nicht kennt, ein Fehler, eine Meldung aus dem
 *                 Prüfstand, eine fertige Webseite, deren Vorschau er ansehen
 *                 soll („bevor etwas live geht“) — und, bis sie veralten, ein
 *                 verpasster Anruf und eine unvollständige Aufnahme. Gleiches
 *                 steht als EINE Zeile mit Zahl und letztem Zeitpunkt; je Zeile
 *                 EINE Handlung.
 *   Fertig        Hintergrund- und Webseiten-Aufträge sind durch (auftrag,
 *                 panel, music) — eine Zeile „7 Dinge fertig“, aufklappbar.
 *   Routine       die wiederkehrenden Berichte (geplant, email) — je Serie EINE
 *                 Zeile mit dem neuesten Lauf und „+ N frühere“
 *
 * Verpasste Anrufe und Aufnahme-Hinweise brauchen keine Handlung: nach GLOCKE_ALTERT_STUNDEN rutschen sie von selbst
 * in „frühere“ — sie dürfen den Punkt nie dauerhaft halten. Innerhalb der 24 Stunden bleibt jeder VERSCHIEDENE Anlass eine
 * eigene Zeile mit seinem Satz (der echte Schreiber legt IMMER „Vespera hat versucht anzurufen: <Anlass>“ ab, nie
 * einen Anruf ohne Anlass — zweite Prüfrunde 07.10.: 10 Anrufe, 9 Anlässe); GLEICHE Anlässe (Schreibweise egal) sind
 * EINE Zeile. Alles Veraltete liegt unter „frühere“ als EIN Bündel, die Anlässe stehen in den Einzelheiten. Alles
 * andere in „Braucht dich“ zählt GLOCKE_ZAEHL_TAGE Tage.
 *
 * Am Knopf zählt nur „Braucht dich“ (Zeilen, nicht Hinweise: vier gleiche verpasste
 * Anrufe sind EINE Zahl). „Fertig“ zeigt die Karte, ohne den Punkt zu setzen — außer
 * einer fertigen WEBSEITE, deren Vorschau Martin prüfen soll: die steht in „Braucht
 * dich“ (Prüfbefund 07.10., Grund der Glocke: Ticket 2331, „Panel-Jobs werden fertig,
 * ohne dass ich es erfahre“). Routine zählt nie.
 *
 * ⚠️ Entschieden vom Integrator, nicht von Martin: andere fertige Arbeit
 *    (Selbstverbesserung, Musik, Aufgaben aus dem Hintergrund) setzt KEINEN Punkt.
 *    Der frühere Satz „Martin sieht das Fertige im Entwicklungs-Ticker“ stimmte nicht:
 *    der Ticker zählt nur Entwicklungs-Tickets und nur für Konto 1 — er ersetzt die
 *    Karte nicht. Ob auch das andere Fertige einen leisen Punkt setzen soll, ist
 *    Martins Entscheidung (offen).
 *
 * ⚠️ Der Grundsatz aus Ticket 2331 gilt weiter (frontend/lib/glocke.ts): die
 *    Glocke zeigt fertige Arbeit und „für dich ist etwas da“ — keine Mahnung.
 *    „Braucht dich“ steht nur für das, was sich mit EINEM Tippen erledigen
 *    lässt, und veraltet von selbst.
 *
 * ⚠️ Eine unbekannte Quelle landet in „Braucht dich“: Vespera darf über
 *    `glocke_hinterlegen` jede Quelle nennen (tools/glocke.ts), und was sie
 *    eigens hinterlegt, ist kein Rauschen. Rauschen, das je so käme, wäre
 *    sofort zu sehen statt still verschluckt.
 *
 * Abhängigkeitsfrei bis auf die Worte (shared/glocke-worte.ts): der Server
 * ordnet (server/glocke-routen.ts), das Frontend nimmt, was er sagt.
 */
import { anrufAnlass, aufnahmeHinweis, braucheVorschau, ohneTagAmEnde, uebersetze, webseiteVon, webseitenName } from "./glocke-worte.js";

type GlockenGruppe = "fuer-dich" | "fertig" | "routine";

/** Zählt nur, was jünger ist als so viele Tage (Martin: „der letzten 3 Tage“). */
export const GLOCKE_ZAEHL_TAGE = 3;

/** Verpasste Anrufe und Aufnahme-Hinweise: nach so vielen Stunden nicht mehr „Braucht dich“. */
export const GLOCKE_ALTERT_STUNDEN = 24;

const STUNDE_MS = 3_600_000;
const TAG_MS = 86_400_000;

/**
 * Quelle → Gruppe und Wort. Die Schlüssel sind am Bestand und an den
 * Schreibern abgelesen (recordHandover/recordHandoverEinmal-Aufrufe), nicht
 * erfunden.
 */
const QUELLEN: Readonly<Record<string, { gruppe: GlockenGruppe; wort: string }>> = {
  system: { gruppe: "fuer-dich", wort: "System" },
  anruf: { gruppe: "fuer-dich", wort: "Anruf" },
  pruefstand: { gruppe: "fuer-dich", wort: "Prüfstand" },
  "nutzer-wunsch": { gruppe: "fuer-dich", wort: "Nutzer-Meldung" },
  vespera: { gruppe: "fuer-dich", wort: "Vespera" },
  auftrag: { gruppe: "fertig", wort: "Hintergrund-Auftrag" },
  panel: { gruppe: "fertig", wort: "Webseite" },
  "server-netz": { gruppe: "fuer-dich", wort: "Server" },
  music: { gruppe: "fertig", wort: "Musik" },
  geplant: { gruppe: "routine", wort: "Geplanter Auftrag" },
  // Einmalige geplante Aufträge und Treffer eines Wächters (server/scheduled-jobs-runner.ts): das erwartet Martin
  // „nur noch in der Glocke“ (seit 17.09.) — sie gehören nicht in die eingeklappte Routine.
  wache: { gruppe: "fuer-dich", wort: "Geplanter Auftrag" },
  email: { gruppe: "routine", wort: "E-Mail" },
};

/** Die Stimmen-Frage schreibt je Stimme eine eigene Quelle („stimme-0cc81939“). */
const STIMME = /^stimme-/;

function quelle(source: string): { gruppe: GlockenGruppe; wort: string } {
  const q = QUELLEN[source];
  if (q) return q;
  if (STIMME.test(source)) return { gruppe: "fuer-dich", wort: "Stimmen" };
  return { gruppe: "fuer-dich", wort: source };
}

/** In welche Gruppe ein Hinweis gehört — nach seiner Quelle. */
export function glockenGruppe(source: string): GlockenGruppe {
  return quelle(source).gruppe;
}

/** Ein Lauf, der nicht gelungen ist: „Fertig“ wäre gelogen, es braucht einen Blick. */
const GESCHEITERT = /gescheitert|fehlgeschlagen|aufgebraucht|abgebrochen|dauert ungewöhnlich lange/i;

/**
 * Die Gruppe eines Eintrags: nach der Quelle — außer
 *   - ein Hinweis aus „Fertig“ meldet im Titel, dass der Lauf nicht gelungen ist („Selbstverbesserung fehlgeschlagen“,
 *     „Auftrag 329 auf Webseite 21: gescheitert“): der kommt zu „Braucht dich“ (06.10.2026, am Bestand 6 von 57);
 *   - eine fertige Webseite verlangt im Satz, dass Martin die Vorschau ansieht (`braucheVorschau`, 07.10.2026):
 *     das ist eine Handlung, also „Braucht dich“.
 * Andere fertige Arbeit bleibt „Fertig“, ohne Punkt.
 */
export function gruppeVon(e: { source: string; title: string; summary?: string }): GlockenGruppe {
  const g = glockenGruppe(e.source);
  if (g !== "fertig") return g;
  if (GESCHEITERT.test(e.title)) return "fuer-dich";
  return braucheVorschau({ source: e.source, title: e.title, summary: e.summary ?? "" }) ? "fuer-dich" : g;
}

/** Woher ein Hinweis kommt — in Worten statt im Kürzel. (Die Karte zeigt es nicht mehr; die Zeile sagt es selbst.) */
export function quelleInWorten(source: string): string {
  return quelle(source).wort;
}

/** Die Laufkennung „(#f2d2b9da)“ am Ende — für Martin ohne Wert. */
const LAUF_AM_ENDE = /\s*\(#[0-9a-f]{8}\)\s*$/i;

/**
 * Der Name einer Serie: der Titel ohne das Datum dahinter.
 * „Tickets durchsehen · 06.10.“ und „Tickets durchsehen · 05.10.“ sind
 * dieselbe Serie; ein Titel ohne Datum ist seine eigene.
 */
export function serienName(title: string): string {
  return ohneTagAmEnde(title) || title.trim();
}

/**
 * Der Titel einer Zeile der alten Liste (Schalter GLOCKE_EINFACH=0): ohne Datum und
 * ohne Laufkennung. „Selbstverbesserung fertig · Ticket #2725 (#f2d2b9da)“ wird zu
 * „Selbstverbesserung fertig · Ticket #2725“. Die einfache Karte nimmt `uebersetze`.
 */
export function anzeigeTitel(title: string): string {
  return serienName(title.replace(LAUF_AM_ENDE, "")) || title.trim();
}

/** Ein Hinweis, wie die Datenbank ihn liefert (handover-store, listeGlocke). */
export interface GlockenEintragRoh {
  id: number;
  source: string;
  title: string;
  summary: string;
  /** ISO mit „Z“ (listeGlocke liefert so aus). */
  createdAt: string;
  gesehen: boolean;
}

/** Eine Serie wiederkehrender Berichte — EINE Zeile in „Routine“. */
export interface GlockenSerie {
  /** Titel ohne Datum. */
  name: string;
  source: string;
  /** Der neueste Lauf. */
  neuester: GlockenEintragRoh;
  /** Wie viele Läufe davor im Fenster liegen („+ N frühere“). */
  frueher: number;
  /** Alle Läufe der Serie — für „Alles gelesen“. */
  ids: number[];
  /** Wie viele davon noch ungesehen sind. */
  ungesehen: number;
}

/** Die EINE Handlung einer Zeile in „Braucht dich“. */
export interface GlockenHandlung {
  /** Das Wort auf dem Knopf. */
  wort: "Okay" | "Ansehen" | "Benennen";
  /**
   * „okay“ hakt nur ab. „Ansehen“ ist ein Link und hakt beim Tippen ab. „Benennen“ hakt NICHT ab: der Hinweis
   * gilt erst als gelesen, wenn die Stimme einen Namen hat (server/index.ts, speakers/name) — oder über
   * „Alles als gelesen markieren“.
   */
  ziel: "okay" | "admin" | "wartet" | "stimmen";
  /** Wohin „Ansehen“ führt, wenn es eine Seite ist (dieselbe Adresse wie die Menüeinträge der App). */
  href?: string;
  /**
   * Nur „Benennen“: der Schlüssel der Frage (die Quelle „stimme-0cc81939“). Mit ihm öffnet die Karte „Sprecher
   * verwalten“ und die Liste dort stellt genau diese Stimme nach oben — auch wenn sie nicht zu den zuletzt gehörten
   * zählt (Prüfbefund 07.10., zweite Runde: die gefragten Stimmen standen in den echten Daten auf Rang 16, 38, 39).
   */
  schluessel?: string;
}

/** Der Ausweg einer Zeile, deren Handlung nicht abhakt: „Okay“ legt den Hinweis weg, ohne etwas zu tun. */
const AUSWEG_OKAY: GlockenHandlung = { wort: "Okay", ziel: "okay" };

/**
 * Was tippen hilft. Nur das, wofür es einen echten Ort gibt: die Meldung eines Nutzers steht im Adminbereich
 * (Abschnitt „Meldungen von Nutzern“), der Prüfstand unter /wartet, eine fremde Stimme benennt man unter „Sprecher
 * verwalten“. Alles andere ist ein „Okay“.
 */
export function handlungFuer(e: { source: string }): GlockenHandlung {
  if (STIMME.test(e.source)) return { wort: "Benennen", ziel: "stimmen", schluessel: e.source };
  if (e.source === "nutzer-wunsch") return { wort: "Ansehen", ziel: "admin", href: "/admin#meldungen" };
  if (e.source === "pruefstand") return { wort: "Ansehen", ziel: "wartet", href: "/wartet#pruefstand" };
  return { wort: "Okay", ziel: "okay" };
}

/** Eine Zeile der Karte — ein Hinweis oder ein Bündel gleichartiger. */
export interface GlockenKarteZeile {
  /** Alle Hinweise dahinter, neueste zuerst (bei einem Bündel mehrere). */
  ids: number[];
  titel: string;
  /** EIN Satz dazu; leer, wenn es keinen gibt — und bei einem Bündel, dessen Hinweise Verschiedenes sagen (dann stehen die Sätze in `einzelne`). */
  text: string;
  /** Der neueste Hinweis dahinter (ISO) — die Karte schreibt „zuletzt vor …“. */
  createdAt: string;
  /** Wie viele Hinweise diese Zeile bündelt. */
  anzahl: number;
  /** In „Braucht dich“: immer wahr. In „Fertig“: ungesehen und jünger als drei Tage. */
  neu: boolean;
  /** Nur in „Braucht dich“ — nur für Neues. */
  handlung: GlockenHandlung | null;
  /**
   * Zweiter Knopf „Okay“ — nur bei einer Handlung, die NICHT abhakt („Benennen“). Nie ein Hinweis, der den Punkt hält
   * und keinen Ausweg hat: wer die Stimme nicht benennen will, legt die Frage mit „Okay“ weg.
   */
  ausweg: GlockenHandlung | null;
  /** Bei einem Bündel (anzahl > 1): je Hinweis ein Satz mit Zeit, für „Einzelheiten“. */
  einzelne: Array<{ id: number; text: string; createdAt: string }>;
}

export interface GlockenOrdnung {
  /** `braucht`: Zeilen in „Braucht dich“ (so viel zeigt der Punkt). `fertig`: neue fertige Dinge. Routine zählt nie. */
  zahl: { braucht: number; fertig: number };
  /** Was Martin tun oder entscheiden soll — nur Neues, gebündelt, Handlungen zuerst. */
  braucht: GlockenKarteZeile[];
  /** Was früher kam, veraltet oder schon gesehen ist (ebenfalls gebündelt) — hinter „frühere anzeigen“. */
  brauchtFrueher: GlockenKarteZeile[];
  /** Fertige Arbeit, neueste zuerst. */
  fertig: GlockenKarteZeile[];
  /** Die Serien, neueste zuerst. */
  routine: GlockenSerie[];
  /** Alles, was die Karte zeigt und noch ungesehen ist — für „Alles als gelesen markieren“. */
  offen: number[];
}

/** Die Zahl an der Glocke: die Zeilen in „Braucht dich“. */
export function glockenZahl(o: Pick<GlockenOrdnung, "zahl">): number {
  return o.zahl.braucht;
}

/** Gleichartiges, das EINE Zeile wird. */
interface Buendel {
  /** Der Schlüssel, unter dem gebündelt wird — oder null, wenn der Hinweis nicht dieser Art ist. */
  schluessel: (e: GlockenEintragRoh) => string | null;
  /** Nach so vielen Stunden braucht es keine Handlung mehr; ohne Angabe gilt GLOCKE_ZAEHL_TAGE. */
  altertNach?: (schluessel: string) => number | undefined;
  /** Der Schlüssel, unter dem VERALTETE Hinweise dieser Art zusammenliegen (ohne Angabe: derselbe wie vorher). */
  veraltet?: (schluessel: string) => string;
  /** Der Titel ab zwei Hinweisen (bei einem sagt `uebersetze`, was da ist). */
  titel: (n: number, schluessel: string) => string;
}

/** Zwei Anlässe sind derselbe, wenn sie sich nur in Groß-/Kleinschreibung, Leerraum und Schlusszeichen unterscheiden. */
function anlassSchluessel(anlass: string): string {
  return anlass.toLowerCase().replace(/\s+/g, " ").replace(/[.!?\s]+$/, "");
}

const BUENDEL: readonly Buendel[] = [
  {
    /*
     * Nur GLEICHE Anlässe sind EIN Bündel, solange der Anruf neu ist (Prüfbefund 07.10.: echte Testanrufe lagen neben
     * einer echten Erinnerung mit Aufgabe, und die Zeile hatte keinen Satz). Nach 24 Stunden verlangt er nichts mehr
     * und legt sich mit allen anderen verpassten Anrufen zu EINER Zeile unter „frühere“ — der Anlass steht dann in den
     * Einzelheiten.
     */
    schluessel: (e) => {
      if (e.source !== "anruf") return null;
      const anlass = anrufAnlass(e.title);
      return anlass === null ? `anruf:?${anlassSchluessel(e.title)}` : `anruf:${anlassSchluessel(anlass)}`;
    },
    altertNach: () => GLOCKE_ALTERT_STUNDEN,
    veraltet: () => "anruf",
    titel: (n) => `Vespera hat ${n}-mal vergeblich angerufen`,
  },
  {
    schluessel: (e) => (e.source === "system" && aufnahmeHinweis(e.title) ? "aufnahme" : null),
    altertNach: () => GLOCKE_ALTERT_STUNDEN,
    titel: (n) => `${n} Aufnahmen waren unvollständig`,
  },
  {
    // Zwei Vorschauen derselben Webseite sind EINE Zeile: eine Vorschau zeigt beide Änderungen.
    schluessel: (e) => {
      const w = braucheVorschau(e) ? webseiteVon(e.title) : null;
      return w ? `vorschau:${w}` : null;
    },
    titel: (n, k) => `${webseitenName(k.slice("vorschau:".length))}: Vorschau ansehen (${n} Änderungen)`,
  },
  {
    // Zwei Änderungen derselben Webseite am selben Tag sind kein Gewinn an Übersicht, wenn sie zweimal dieselbe Zeile sind.
    schluessel: (e) => {
      const w = e.source === "panel" && /: fertig$/.test(e.title) ? webseiteVon(e.title) : null;
      return w ? `webseite:${w}` : null;
    },
    titel: (n, k) => `${webseitenName(k.slice("webseite:".length))}: ${n} Änderungen fertig`,
  },
];

function buendelVon(e: GlockenEintragRoh): { b: Buendel; schluessel: string } | null {
  for (const b of BUENDEL) {
    const schluessel = b.schluessel(e);
    if (schluessel) return { b, schluessel };
  }
  return null;
}

/** Wie lange ein Hinweis „neu“ sein darf, in Millisekunden. */
function neuBisMs(e: GlockenEintragRoh): number {
  const art = buendelVon(e);
  const stunden = art?.b.altertNach?.(art.schluessel);
  return stunden === undefined ? GLOCKE_ZAEHL_TAGE * TAG_MS : stunden * STUNDE_MS;
}

/** Ein Hinweis, mit dem Urteil „neu“ (ungesehen und noch nicht veraltet). */
interface Beurteilt {
  e: GlockenEintragRoh;
  neu: boolean;
}

/** Eine Zeile aus gleichartigen Hinweisen (neueste zuerst) — mindestens einer. */
function baueZeile(gruppe: [Beurteilt, ...Beurteilt[]], mitHandlung: boolean): GlockenKarteZeile {
  const [neuester] = gruppe;
  const bund = gruppe.length > 1 ? buendelVon(neuester.e) : null;
  const worte = uebersetze(neuester.e);
  const einzelne = bund ? gruppe.map((x) => ({ id: x.e.id, text: uebersetze(x.e).text, createdAt: x.e.createdAt })) : [];
  // Sagen alle Hinweise dasselbe („Schau dir die Vorschau an …“), steht der Satz EINMAL bei der Zeile — die Einzelheiten tragen nur die Zeit.
  // „Gleich“ heißt: bis auf Schreibweise (Groß-/Kleinschreibung, Leerraum, Schlusszeichen) — derselbe Anlass trägt dann seinen Satz.
  const erster = einzelne[0]?.text ?? "";
  const gleich = bund && erster !== "" && einzelne.every((x) => anlassSchluessel(x.text) === anlassSchluessel(erster)) ? erster : "";
  const handlung = mitHandlung && neuester.neu ? handlungFuer(neuester.e) : null;
  return {
    ids: gruppe.map((x) => x.e.id),
    titel: bund ? bund.b.titel(gruppe.length, bund.schluessel) : worte.titel,
    text: bund ? gleich : worte.text,
    createdAt: neuester.e.createdAt,
    anzahl: gruppe.length,
    neu: neuester.neu,
    handlung,
    ausweg: handlung?.ziel === "stimmen" ? AUSWEG_OKAY : null,
    einzelne: gleich ? einzelne.map((x) => ({ ...x, text: "" })) : einzelne,
  };
}

/**
 * Hinweise zu Zeilen. Was derselben Art ist UND gleich neu, wird EINE Zeile; alles andere bleibt je Hinweis eine.
 * Die Reihenfolge ist die der Eingabe (neueste zuerst): eine Zeile steht dort, wo ihr neuester Hinweis steht.
 */
function baueZeilen(liste: Beurteilt[], mitHandlung: boolean): GlockenKarteZeile[] {
  const gruppen = new Map<string, [Beurteilt, ...Beurteilt[]]>();
  for (const x of liste) {
    const b = buendelVon(x.e);
    // Was veraltet ist, liegt je Art zusammen (alle verpassten Anrufe in EINER Zeile, ihre Anlässe in den Einzelheiten).
    const art = b ? (x.neu ? b.schluessel : (b.b.veraltet?.(b.schluessel) ?? b.schluessel)) : `einzeln:${x.e.id}`;
    const schluessel = `${art}|${x.neu ? "neu" : "alt"}`;
    const g = gruppen.get(schluessel);
    if (g) g.push(x);
    else gruppen.set(schluessel, [x]);
  }
  return [...gruppen.values()].map((g) => baueZeile(g, mitHandlung));
}

/**
 * Ordnet, was im Fenster liegt. Ändert nichts, liest keine Uhr selbst.
 *
 * ⚠️ Das Sieben-Tage-Fenster zieht die Abfrage (handover-store, listeGlocke),
 *    nicht diese Funktion — EINE Stelle für die Altersgrenze. Hier wird nur
 *    entschieden, was davon noch „neu“ ist.
 */
export function ordneGlocke(alle: readonly GlockenEintragRoh[], jetztMs: number): GlockenOrdnung {
  const neuesteZuerst = [...alle].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt) || b.id - a.id);
  const serien = new Map<string, GlockenSerie>();
  const fuerDich: Beurteilt[] = [];
  const fertig: Beurteilt[] = [];

  for (const e of neuesteZuerst) {
    const gruppe = gruppeVon(e);
    if (gruppe === "routine") {
      const name = serienName(e.title);
      const schluessel = `${e.source}\u0000${name}`;
      const s = serien.get(schluessel);
      if (s) {
        s.frueher += 1;
        s.ids.push(e.id);
        if (!e.gesehen) s.ungesehen += 1;
      } else {
        serien.set(schluessel, { name, source: e.source, neuester: e, frueher: 0, ids: [e.id], ungesehen: e.gesehen ? 0 : 1 });
      }
      continue;
    }
    const zeit = Date.parse(e.createdAt);
    const neu = !e.gesehen && Number.isFinite(zeit) && zeit > jetztMs - neuBisMs(e);
    (gruppe === "fuer-dich" ? fuerDich : fertig).push({ e, neu });
  }

  // Handlungen zuerst (die Stimme, der Fehler), dann das, was nur „Okay“ will; sonst die Reihenfolge der Zeit.
  const hatHandlung = (z: GlockenKarteZeile): number => (z.handlung && z.handlung.ziel !== "okay" ? 0 : 1);
  const braucht = baueZeilen(fuerDich.filter((x) => x.neu), true)
    .map((z, i) => ({ z, i }))
    .sort((a, b) => hatHandlung(a.z) - hatHandlung(b.z) || a.i - b.i)
    .map(({ z }) => z);
  const fertigZeilen = baueZeilen(fertig, false);

  return {
    zahl: {
      braucht: braucht.length,
      fertig: fertigZeilen.filter((z) => z.neu).reduce((n, z) => n + z.anzahl, 0),
    },
    braucht,
    brauchtFrueher: baueZeilen(fuerDich.filter((x) => !x.neu), false),
    fertig: fertigZeilen,
    routine: [...serien.values()],
    offen: neuesteZuerst.filter((e) => !e.gesehen).map((e) => e.id),
  };
}
